import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MapPin, X, History, Globe, Building } from 'lucide-react';
import LocationSuggestions, { LocationType } from './LocationSuggestions';
import FeaturedLocations from './FeaturedLocations';

const RECENT_LOCATIONS_KEY = 'kayros_recent_locations';
const MAX_RECENT_LOCATIONS = 5;

const EnhancedLocationSearch = ({ value, onChange, onSelect }) => {
  const [searchQuery, setSearchQuery] = useState(value || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentLocations, setRecentLocations] = useState([]);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  console.log("EnhancedLocationSearch state:", { searchQuery, showSuggestions, recentLocations });

  // Cargar ubicaciones recientes al montar el componente
  useEffect(() => {
    try {
      const savedLocations = localStorage.getItem(RECENT_LOCATIONS_KEY);
      if (savedLocations) {
        setRecentLocations(JSON.parse(savedLocations));
      }
    } catch (error) {
      console.error('Error loading recent locations:', error);
    }
  }, []);

  // Cerrar sugerencias al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Guardar ubicación en recientes
  const saveToRecent = (location) => {
    try {
      const newLocations = [
        location,
        ...recentLocations.filter(item => item.id !== location.id)
      ].slice(0, MAX_RECENT_LOCATIONS);
      
      setRecentLocations(newLocations);
      localStorage.setItem(RECENT_LOCATIONS_KEY, JSON.stringify(newLocations));
    } catch (error) {
      console.error('Error saving location to recents:', error);
    }
  };

  // Manejar selección de ubicación
  const handleLocationSelect = (location) => {
    console.log("Location selected:", location);
    
    // Si es una ciudad, mostrar "Ciudad, País"
    let displayText = location.display;
    
    if (location.type === LocationType.CITY && location.secondaryText) {
      displayText = `${location.display}, ${location.secondaryText}`;
    }
    
    setSearchQuery(displayText);
    onChange(displayText);
    onSelect(location);
    saveToRecent(location);
    setShowSuggestions(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/60 w-5 h-5" />
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            onChange(e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          placeholder="Buscar por ciudad, país..."
          className="w-full pl-12 pr-10 py-3 rounded-lg bg-white/10 text-white placeholder-white/60
                   border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/30"
        />
        
        {/* Botón para limpiar la ubicación */}
        {searchQuery && (
          <button
            type="button"
            onClick={() => {
              setSearchQuery('');
              onChange('');
              onSelect(null);
              inputRef.current.focus();
            }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 
                    hover:text-white focus:outline-none transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Panel de sugerencias */}
      {showSuggestions && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden max-h-96 overflow-y-auto">
          {/* Mostrar ciudades recientes si no hay búsqueda */}
          {!searchQuery && recentLocations.length > 0 && (
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center gap-2 mb-3 text-gray-700">
                <History className="w-4 h-4" />
                <span className="text-sm font-medium">Búsquedas recientes</span>
              </div>
              
              <div className="flex flex-col gap-2">
                {recentLocations.map((location) => {
                  const Icon = location.type === LocationType.CITY ? Building : Globe;
                  
                  return (
                    <div
                      key={`${location.type}-${location.id}`}
                      className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                      onClick={() => handleLocationSelect(location)}
                    >
                      <Icon className="w-4 h-4 text-indigo-500" />
                      <div>
                        <span className="font-medium text-gray-800">{location.display}</span>
                        {location.secondaryText && (
                          <span className="text-gray-500 text-sm ml-1">
                            ({location.secondaryText})
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Mostrar ciudades destacadas si no hay búsqueda */}
          {!searchQuery && <FeaturedLocations onSelectLocation={handleLocationSelect} />}

          {/* Sugerencias basadas en la búsqueda actual */}
          {searchQuery && (
            <LocationSuggestions 
              query={searchQuery}
              onSelectLocation={handleLocationSelect}
            />
          )}
          
          {/* Si no hay resultados pero hay búsqueda, mostrar mensaje */}
          {searchQuery && !recentLocations.length && (
            <div className="p-4 text-center text-gray-500">
              <p>Escribe para buscar ciudades...</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EnhancedLocationSearch;